import axios from 'axios';
import { apiRoute } from '../constants/api-constants';

/* ───────────────────────────────────────────────────────────────────────────
 * CHANGING PLAN (WP-23)
 *
 * The billing page used to POST /subscriptions/checkout and then do
 * `window.location.href = data.url` — a full-page redirect out of the app to
 * checkout.stripe.com. Hosted Checkout in subscription mode CREATES a
 * subscription, and nothing cancelled the old one, so a plan change by an
 * already-invoiced customer billed them twice for the same month: the old
 * period was paid, a bare cancel issues no credit, and the new subscription
 * starts its own billing.
 *
 * So a plan change is now `stripe.subscriptions.update` on the subscription the
 * org already has, applied server-side, and the browser never leaves the app.
 *
 * THE HOSTED FLOW IS NOT DELETED. POST /subscriptions/checkout still exists for
 * the case it is safe for — an org with nothing at Stripe yet — and it now
 * REFUSES an org that already has a subscription (409 HAS_LIVE_SUBSCRIPTION)
 * rather than quietly double-billing it. `reconcileCheckout` below is the
 * return leg, and it is the ONLY activation path on a box where Stripe webhooks
 * cannot arrive (locally STRIPE_WEBHOOK_SECRET is empty and the webhook route
 * 503s every event before parsing it).
 *
 * Uses the DEFAULT axios instance so these authenticated calls inherit the
 * interceptors in axios-setup.ts (credentials, CSRF header, 401 -> refresh ->
 * retry), like units-client.ts and for the same reason.
 * ─────────────────────────────────────────────────────────────────────────── */

// What GET /subscriptions/plan/preview returns.
//
// FOUR FIGURES THAT ARE NOT INTERCHANGEABLE, the same contract the units
// preview established (owner decision D-12):
//
//   rate_cents                 per unit per interval on the TARGET plan
//   new_recurring_total_cents  rate x quantity — what the org pays from now on
//   amount_due_now_cents       what Stripe captures at this instant. 0 under
//                              create_prorations
//   proration_cents            the adjustment that lands on the NEXT invoice
//
// `null` means NOT KNOWN and must never be rendered as "$0.00" — Stripe refuses
// to preview an invoice for a card-less trial at all, which is a real answer
// about that org and not an outage.
//
// NOTHING IN THE FRONTEND MULTIPLIES A RATE BY A COUNT.
export interface PlanChangePreview {
  plan_id: number | null;
  plan_code: string | null;
  plan_name: string | null;
  current_plan_id: number | null;
  current_plan_name: string | null;
  quantity: number;
  rate_cents: number;
  current_rate_cents: number;
  currency: string;
  interval: string;
  current_recurring_total_cents: number;
  new_recurring_total_cents: number;
  proration_cents: number | null;
  amount_due_now_cents: number | null;
  amount_due_now_error: string | null;
  // charge_now only: adjustments from EARLIER changes (a return's credit, say)
  // still waiting for the next regular invoice. Not part of today's charge.
  pending_adjustments_cents?: number | null;
  next_invoice_total_cents: number | null;
  next_invoice_at: string | null;
  // The instant (unix SECONDS) Stripe priced this quote at, sent back on the
  // commit so the change is prorated from the moment the user was shown.
  // Null when nothing was priced; optional for an older server.
  proration_date?: number | null;
  // Lobbero IQ is switched by subscriptions.iq_enabled, which this change
  // writes. Surfaced so a downgrade cannot silently remove a feature in use.
  iq_now: boolean;
  iq_after: boolean;
  iq_change: 'gained' | 'lost' | 'none';
  // The org's OWN trial, which a plan change never moves — updating an existing
  // subscription leaves it exactly where it was. There is no "fresh 30 days"
  // here; that was the hosted-checkout defect.
  trial_end: string | null;
  trial_preserved: boolean;
  requires_card: boolean;
  // Monthly / annual (migration 105). An interval change is a plan change onto
  // a row with a different interval; these say which way it goes and when.
  interval_now?: string | null;
  interval_after?: string | null;
  change_type?: 'tier' | 'to_annual' | 'to_monthly' | null;
  // Now, or the end of the paid year for a 'scheduled' annual -> monthly.
  effective_at?: string | null;
  // Promotions an annual plan does not carry — removed with the change, and
  // named here so the customer sees it before confirming.
  discounts_removed?: { id: string; description: string }[];
  // 'trial'      nothing is charged now; the new rate begins at trial_end
  // 'proration'  the adjustment lands on the next invoice
  // 'charge_now' the prorated difference is invoiced and paid on confirm —
  //              amount_due_now_cents is exactly that invoice (Stripe's own
  //              always_invoice preview)
  // 'scheduled'  nothing changes until effective_at (annual -> monthly)
  // 'refuse'     `reason` says why, and every refusal leaves the plan alone
  mode: 'trial' | 'proration' | 'charge_now' | 'scheduled' | 'refuse';
  // On a PAYMENT_PENDING refusal: the page that completes the waiting payment.
  payment?: { hosted_invoice_url: string | null; amount_due_cents: number | null } | null;
  reason?: string | null;
  message?: string | null;
  // Present only on a NO_PAYMENT_METHOD refusal: what adding a card would do.
  // Absent means the refusal is NOT recoverable from this screen — which is
  // also what an older server sends — so its absence must disable the change
  // rather than silently open a card form with no intent behind it.
  recovery?: {
    available: boolean;
    first_charge_cents: number;
    trial_preserved: boolean;
    starts_at: string | null;
  } | null;
}

export interface PlanChangeResult {
  mode?: 'trial' | 'proration' | 'charge_now' | 'scheduled';
  plan_id: number;
  plan_code: string;
  plan_name: string;
  iq_enabled: boolean;
  interval_after?: string;
  quantity: number;
  rate_cents: number;
  new_recurring_total_cents: number;
  // What Stripe captured at the instant of the change (charge_now only).
  charged_now_cents?: number;
  // A 'scheduled' change names the plan it will switch to, and when.
  scheduled_plan_name?: string;
  effective_at?: string | null;
  discounts_removed?: { id: string; description: string }[];
  status: string | null;
  trial_end: string | null;
}

// A COMPLIMENTARY ACCOUNT HAS NO BILLING TO CHANGE (migration 110).
//
// A comped organisation (FREE_SIGNUP) is live and entitled but has no Stripe
// customer, subscription or card, and the server refuses EVERY billing mutation
// for it — plan, units, returns, card, promo, cancel, resume, checkout — with
// 409 and code COMPED_ACCOUNT (lobbero_be src/CRUD/plans.js, refuseComped).
// The billing screen hides those actions for a comped account; this is the
// answer for any path that reaches one anyway (a tab opened before the account
// was comped, the out-of-units dialog, which cannot see the flag), so the
// reader is told what is true rather than "please try again".
export const COMPED_ACCOUNT_MESSAGE =
  'This account is on a complimentary plan, so there is no billing to change. ' +
  'To move it onto a paid subscription, contact us at hello@lobbero.com.';

/** The complimentary-plan sentence when `err` is the server's COMPED_ACCOUNT
 *  refusal, otherwise null. Read by shape, not through axios.isAxiosError: the
 *  error can come from any client instance, and the tests' axios mocks do not
 *  carry the helper. */
export const compedRefusal = (err: unknown): string | null => {
  const e = err as { response?: { data?: { code?: unknown } } };

  return e && e.response && e.response.data && e.response.data.code === 'COMPED_ACCOUNT'
    ? COMPED_ACCOUNT_MESSAGE
    : null;
};

// DELETE /subscriptions/plan/scheduled — "keep annual": take back a switch to
// monthly before it happens. The subscription stays exactly as it is.
export const cancelScheduledChange = (): Promise<{ released: boolean }> =>
  axios
    .delete<{ released: boolean }>(`${apiRoute}/subscriptions/plan/scheduled`)
    .then(r => r.data);

// POST /subscriptions/checkout — the RESUBSCRIBE path for an organisation whose
// subscription has ended. The server refuses an org that still has a live
// subscription (HAS_LIVE_SUBSCRIPTION) — a plan change is the path for those —
// and never re-grants a trial to an org that already had one.
export const startCheckout = (planId: number): Promise<{ url: string }> =>
  axios
    .post<{ url: string }>(`${apiRoute}/subscriptions/checkout`, { plan_id: planId })
    .then(r => r.data);

export const previewPlanChange = (planId: number): Promise<PlanChangePreview> =>
  axios
    .get<PlanChangePreview>(`${apiRoute}/subscriptions/plan/preview`, {
      params: { plan_id: planId },
    })
    .then(r => r.data);

// POST /subscriptions/plan — apply the change.
//
// The server updates the EXISTING Stripe subscription onto the new price with
// explicit proration and then mirrors two columns locally. It never creates a
// subscription and never cancels one, so there is no window in which an org has
// two live subscriptions.
//
// `setupIntentId` is the OPTIONAL proof that a card was just confirmed, the
// same one the unit purchase uses: retrieved from Stripe server-side, bound to
// this organisation and spent exactly once. It is only ever needed for an
// UPGRADE by an org with no card on file — refusing a DOWNGRADE for want of a
// card would trap an org in the more expensive plan.
//
// Refusals, all 4xx/5xx with a `code`, and every one of them means the plan was
// left exactly as it was: PLAN_NOT_FOUND, PLAN_IS_CONTACT_SALES,
// PLAN_NOT_PURCHASABLE, PLAN_UNCHANGED, NO_SUBSCRIPTION, NO_STRIPE_SUBSCRIPTION,
// NO_PAYMENT_METHOD, PROOF_NOT_CONFIRMED, PROOF_ALREADY_USED, PROOF_NOT_YOURS,
// LIVE_ROW_MISMATCH, STRIPE_SUBSCRIPTION_INCOMPLETE, STRIPE_UPDATE_UNCONFIRMED,
// PLAN_LINK_FAILED, STRIPE_NOT_CONFIGURED.
//
// `prorationDate` is the confirmed preview's `proration_date`, so the charge is
// the one that was shown. Omitted when the preview carried none.
export const changePlan = (
  planId: number,
  setupIntentId?: string | null,
  prorationDate?: number | null
): Promise<PlanChangeResult> =>
  axios
    .post<PlanChangeResult>(`${apiRoute}/subscriptions/plan`, {
      plan_id: planId,
      ...(setupIntentId ? { setup_intent: setupIntentId } : {}),
      ...(prorationDate != null ? { proration_date: prorationDate } : {}),
    })
    .then(r => r.data);

export interface ReconcileResult {
  activated: boolean;
  already?: boolean;
  synced?: boolean;
  reason?: string;
  status?: string;
  quantity?: number | null;
  plan_id?: number | null;
  plan_code?: string | null;
  iq_enabled?: boolean | null;
}

// POST /subscriptions/reconcile — THE HOSTED FLOW'S RETURN LEG.
//
// Stripe redirects back to /dashboard/billing?checkout=success&session_id=…
// and that session id is what activates the subscription locally. The billing
// page used to DISCARD it and only show a toast, so on any box without a
// webhook secret the hosted path was a silent no-op: the user paid, came back,
// saw "Subscription updated", and nothing had changed.
//
// Called with no session id it re-reads the org's OWN subscription from Stripe
// (the id comes from the caller's row under RLS, never from the request body)
// and syncs status, period, quantity and — since WP-23 — plan and iq_enabled.
// GET /billing/entitlement — the one billing fact every staff member may
// read: is the desk open? The main menu freezes on `active: false` (the
// subscription is over); the server enforces the same gate with 402
// SUBSCRIPTION_ENDED on every desk write, so this is presentation, not
// security. Fails OPEN like the server does: a transient error must not
// paint "subscription ended" over a paying customer's desk.
export const getEntitlement = (): Promise<{ active: boolean }> =>
  axios
    .get<{ active: boolean }>(`${apiRoute}/billing/entitlement`)
    .then(r => r.data)
    .catch(() => ({ active: true }));

export const reconcileCheckout = (
  sessionId?: string | null
): Promise<ReconcileResult> =>
  axios
    .post<ReconcileResult>(
      `${apiRoute}/subscriptions/reconcile`,
      sessionId ? { session_id: sessionId } : {}
    )
    .then(r => r.data);
